import { useEffect, useState, useRef } from "react";
import { Send, MessageSquare, User, History, Radio, Monitor, Smartphone, Tablet, MapPin, UserCheck, Save } from "lucide-react";
import api, { WS_BASE, fileUrl } from "@/lib/api";
import { toast } from "sonner";

const DeviceIcon = ({ d, ...p }) => (d === "mobile" ? <Smartphone {...p} /> : d === "tablet" ? <Tablet {...p} /> : <Monitor {...p} />);
const STATUS_BADGE = (s) => s === "queued" ? "bg-red-100 text-red-600" : s === "live" ? "bg-green-100 text-green-700" : s === "waiting" ? "bg-amber-100 text-amber-700" : "bg-stone/15 text-stone";
const STATUS_LABEL = (s) => ({ queued: "En file", live: "En cours", waiting: "En attente", bot: "Bot / terminé" }[s] || s);

function QualifyForm({ session, onSaved }) {
  const v = session.visitor || {};
  const [f, setF] = useState({ first_name: v.first_name || "", last_name: v.last_name || "", phone: v.phone || "", email: v.email || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/chat/sessions/${session.session_id}/qualify`, f); toast.success("Visiteur qualifié"); onSaved(data); }
    catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  const inp = "w-full rounded-lg border border-border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";
  return (
    <div className="rounded-xl border border-brand/30 bg-brand/5 p-3 space-y-2" data-testid="qualify-form">
      <p className="text-xs font-bold text-brand flex items-center gap-1.5"><UserCheck size={13} /> Qualifier / identifier</p>
      <div className="grid grid-cols-2 gap-2">
        <input className={inp} placeholder="Prénom" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="qualify-firstname" />
        <input className={inp} placeholder="Nom" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="qualify-lastname" />
        <input className={inp} placeholder="Téléphone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="qualify-phone" />
        <input className={inp} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="qualify-email" />
      </div>
      <button onClick={save} disabled={saving} data-testid="qualify-save" className="w-full rounded-lg bg-brand text-cream py-1.5 text-sm font-semibold hover:bg-forest flex items-center justify-center gap-1.5 disabled:opacity-60"><Save size={14} /> Enregistrer</button>
    </div>
  );
}

export default function LiveChat() {
  const [tab, setTab] = useState("live");
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);
  const roomWs = useRef(null);

  const token = () => localStorage.getItem("aj_token") || "";
  const loadSessions = () => {
    const url = tab === "history" ? "/chat/history" : "/chat/sessions";
    api.get(url).then((r) => setSessions(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadSessions();
    if (tab === "live") {
      const lobby = new WebSocket(`${WS_BASE}/ws/agent-lobby?token=${token()}`);
      lobby.onmessage = () => loadSessions();
      const t = setInterval(loadSessions, 15000);
      return () => { lobby.close(); clearInterval(t); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (!active) return;
    api.get(`/chat/${active.session_id}/messages`).then((r) => setMessages(r.data));
    if (tab === "history") { roomWs.current = null; return; }
    const ws = new WebSocket(`${WS_BASE}/ws/chat/${active.session_id}?role=agent&token=${token()}`);
    roomWs.current = ws;
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === "message") {
        setMessages((m) => [...m, { role: data.role, content: data.content, image: data.image, agent_name: data.agent_name }]);
      } else if (data.type === "delta") {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last && last.role === "assistant" && last.streaming) copy[copy.length - 1] = { ...last, content: last.content + data.content };
          else copy.push({ role: "assistant", content: data.content, streaming: true });
          return copy;
        });
      } else if (data.type === "done") {
        setMessages((m) => m.map((x) => (x.streaming ? { ...x, streaming: false } : x)));
      }
    };
    return () => { ws.close(); roomWs.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [messages]);

  const send = () => {
    const msg = input.trim();
    if (!msg || !active || roomWs.current?.readyState !== WebSocket.OPEN) return;
    setInput("");
    setMessages((m) => [...m, { role: "agent", content: msg, agent_name: "Vous" }]);
    roomWs.current.send(JSON.stringify({ type: "message", content: msg }));
  };

  return (
    <div>
      <h1 className="font-display text-4xl text-forest mb-1">Chat en direct</h1>
      <p className="text-stone mb-4">Conversations en temps réel + historique complet des visiteurs</p>

      <div className="flex gap-2 mb-4 border-b border-border">
        <button onClick={() => { setTab("live"); setActive(null); }} data-testid="chat-tab-live"
          className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 flex items-center gap-1.5 ${tab === "live" ? "border-brand text-brand" : "border-transparent text-stone hover:text-forest"}`}><Radio size={15} /> En direct</button>
        <button onClick={() => { setTab("history"); setActive(null); }} data-testid="chat-tab-history"
          className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 flex items-center gap-1.5 ${tab === "history" ? "border-brand text-brand" : "border-transparent text-stone hover:text-forest"}`}><History size={15} /> Historique</button>
      </div>

      <div className="grid md:grid-cols-[340px_1fr] gap-5 rounded-xl bg-white border border-border overflow-hidden" style={{ minHeight: 540 }}>
        <div className="border-r border-border overflow-y-auto" data-testid="chat-session-list">
          {sessions.length === 0 && <p className="text-stone text-sm p-6 text-center">{tab === "history" ? "Aucune conversation." : "Aucune conversation active. Passez « En ligne »."}</p>}
          {sessions.map((s) => (
            <button key={s.id} onClick={() => setActive(s)} data-testid={`session-${s.session_id}`}
              className={`w-full text-left px-4 py-3 border-b border-border flex items-start gap-3 transition-colors ${active?.session_id === s.session_id ? "bg-sage" : "hover:bg-[#F7F7F8]"}`}>
              <div className="h-9 w-9 rounded-full bg-brand/10 flex items-center justify-center shrink-0"><User size={16} className="text-brand" /></div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-forest truncate">{s.visitor?.name || (s.visitor?.qualified ? "Visiteur qualifié" : "Visiteur")}</p>
                <p className="text-xs text-stone truncate">{s.last_message || "…"}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_BADGE(s.status)}`}>{STATUS_LABEL(s.status)}</span>
                  {s.device && <span className="text-[10px] text-stone flex items-center gap-0.5"><DeviceIcon d={s.device} size={11} /> {s.device}</span>}
                  {tab === "history" && <span className="text-[10px] text-stone">{s.message_count} msg</span>}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-stone"><div className="text-center"><MessageSquare className="mx-auto mb-2 text-stone/40" size={32} /><p>Sélectionnez une conversation</p></div></div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-border flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-forest">{active.visitor?.name || "Visiteur"}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-stone mt-0.5">
                    {active.visitor?.email && <span>{active.visitor.email}</span>}
                    {active.visitor?.phone && <span>{active.visitor.phone}</span>}
                    {active.ip && <span className="flex items-center gap-1"><MapPin size={11} /> {active.ip}</span>}
                    {active.device && <span className="flex items-center gap-1"><DeviceIcon d={active.device} size={11} /> {active.device}</span>}
                  </div>
                </div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: 360 }}>
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.role === "user" ? "bg-sage text-forest" : m.role === "agent" ? "bg-brand text-cream" : "bg-[#F0EFEA] text-stone italic"}`}>
                      {m.role === "assistant" && <span className="block text-[10px] font-bold opacity-60 mb-0.5">Flora (IA)</span>}
                      {m.image && <img src={fileUrl(m.image)} alt="photo" className="rounded-lg mb-1 max-h-40 object-cover" data-testid="chat-msg-img" />}
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-border p-3 space-y-3">
                <QualifyForm session={active} onSaved={(d) => { setActive(d); loadSessions(); }} />
                {tab === "live" && (
                  <div className="flex gap-2">
                    <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre réponse…" data-testid="agent-input" className="flex-1 rounded-full border border-border px-4 py-2.5 text-sm" />
                    <button onClick={send} data-testid="agent-send" className="h-10 w-10 rounded-full bg-brand text-cream flex items-center justify-center hover:bg-forest"><Send size={17} /></button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
